import React, { useState, useEffect, useRef } from 'react';
import {
  IonModal,IonHeader,IonToolbar,IonTitle,IonButtons,IonButton,IonIcon,IonContent,IonInput,IonItem,IonLabel,IonSpinner
} from '@ionic/react';
import { close, addOutline, removeOutline, checkmarkCircle, alertCircle, cameraOutline } from 'ionicons/icons';
import { useInvitation } from '../../contexts/InvitationContext';
import { uploadTeamLogo, loadTeamFromProfile } from '../../services/teamService';
import { useProfileStore } from '../../store/useProfileStore';
import { showInfo } from '../../services/toastService';
import { useAccountForm, getAccountFormUrl } from '../../utils/accounts';

export interface InviteInput {
  email: string;
  displayName: string;
}

interface InviteTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  usedSeats: number;
  totalSeats: number;
  teamName?: string;
  teamLogoUrl?: string | null;
  seatPriceMonthly?: number;
  seatPriceAnnual?: number;
  onSaveTeamName?: (teamName: string) => void;
  onInvitationsSent?: () => Promise<void> | void;
}

export function InviteTeamModal({
  isOpen,
  onClose,
  usedSeats,
  totalSeats,
  teamName: initialTeamName,
  teamLogoUrl,
  seatPriceMonthly,
  seatPriceAnnual,
  onSaveTeamName,
  onInvitationsSent
}: InviteTeamModalProps) {
  const [teamName, setTeamName] = useState(initialTeamName || '');
  const [isEditingTeamName, setIsEditingTeamName] = useState(!initialTeamName);
  const [inviteInputs, setInviteInputs] = useState<InviteInput[]>([{ email: '', displayName: '' }]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(teamLogoUrl || null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { sendInvitations, isInviting, inviteResults, clearInviteResults } = useInvitation();
  const { setActiveTeam } = useProfileStore();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTeamName(initialTeamName || '');
      setIsEditingTeamName(!initialTeamName);
      setInviteInputs([{ email: '', displayName: '' }]);
      setShowConfirmation(false);
      setLogoPreview(teamLogoUrl || null);
      clearInviteResults();
    }
  }, [isOpen, initialTeamName, teamLogoUrl, clearInviteResults]);

  const handleLogoClick = () => {
    if (!initialTeamName) {
      showInfo(
        'Enter a Team name and click save to create your team before you upload a logo.',
        [], { position: 'top' }
      );
      return;
    }
    fileInputRef.current?.click();
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      setIsUploadingLogo(true);
      await uploadTeamLogo(file);
      const reloadedTeam = await loadTeamFromProfile();
      if (reloadedTeam) {
        setActiveTeam(reloadedTeam);
        setLogoPreview(reloadedTeam.logoUrl || null);
      }
    } catch (error) {
      setLogoPreview(teamLogoUrl || null);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Calculate available seats (X = totalSeats - usedSeats)
  const availableSeats = totalSeats - usedSeats;
  const showAddSeatsButton = totalSeats < 1 || useAccountForm();
  const seatPrice = (seatPriceMonthly ?? 0) > 0 ? seatPriceMonthly : ((seatPriceAnnual ?? 0) > 0 ? seatPriceAnnual : 0);
  const seatPeriod = (seatPriceMonthly ?? 0) > 0 ? 'month' : 'year';

  // Calculate additional seats needed if inviting over the limit
  const validInvitesCount = inviteInputs.filter(
    invite => invite.email.trim() !== '' && invite.displayName.trim() !== ''
  ).length;
  
  const additionalSeatsNeeded = validInvitesCount > availableSeats 
    ? validInvitesCount - availableSeats 
    : 0;

  // Check if team name exists and is saved
  // Team name is saved if: initialTeamName exists (passed as prop) OR teamName exists and not in edit mode
  const hasTeamName = initialTeamName || (teamName.trim() && !isEditingTeamName);

  const handleTeamNameChange = (value: string) => {
    setTeamName(value);
  };

  const handleSaveTeamName = () => {
    if (teamName.trim() && onSaveTeamName) {
      onSaveTeamName(teamName.trim());
      setIsEditingTeamName(false);
    }
  };

  const handleEditTeamName = () => {
    setIsEditingTeamName(true);
  };

  const handleInputChange = (index: number, field: 'email' | 'displayName', value: string) => {
    const newInputs = [...inviteInputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setInviteInputs(newInputs);
  };

  const handleAddInvite = () => {
    setInviteInputs([...inviteInputs, { email: '', displayName: '' }]);
  };

  const handleRemoveInvite = (index: number) => {
    if (inviteInputs.length > 1) {
      const newInputs = inviteInputs.filter((_, i) => i !== index);
      setInviteInputs(newInputs);
    }
  };

  const handleInvite = async () => {
    // Require team name to be saved before inviting
    if (!hasTeamName) {
      showInfo(
        'Please create a team name first before inviting team members.',
        [],
        { position: 'top' }
      );
      return;
    }

    // Filter out incomplete invites (need both email and displayName)
    const validInvites = inviteInputs.filter(
      invite => invite.email.trim() !== '' && invite.displayName.trim() !== ''
    );
    
    if (validInvites.length === 0) {
      return;
    }

    await sendInvitations(validInvites);
    setShowConfirmation(true);
    
    // Refresh team data asynchronously to show newly invited users with pending status
    if (onInvitationsSent) {
      // Don't await - let it refresh in the background
      onInvitationsSent();
    }
  };

  const handleDone = () => {
    setInviteInputs([{ email: '', displayName: '' }]);
    setShowConfirmation(false);
    clearInviteResults();
    onClose();
  };

  const handleAddSeats = () => {
    if (useAccountForm()) {
      const accountFormUrl = getAccountFormUrl();
      if (accountFormUrl) {
        window.open(accountFormUrl, '_blank');
      }
    } else {
      // TODO implmement in-app purchase flow
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle style={{ color: '#D44D5C', fontWeight: 'bold', fontSize: '1.25rem' }}>
            Invite Your Team!
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {showConfirmation ? (
          /* Confirmation View */
          <div className="flex flex-col gap-4">
            <div className="text-center py-4">
              <IonIcon 
                icon={checkmarkCircle} 
                className="w-16 h-16 text-green-500 mx-auto mb-4"
              />
              <h2 className="text-xl font-semibold text-[#2D2D2D] mb-2">
                Invitations Sent!
              </h2>
              <p className="text-[#928881]">
                Your team members will receive an email invitation shortly.
              </p>
            </div>
            
            {/* Results List */}
            <div className="flex flex-col gap-2">
              {Array.from(inviteResults.values()).map((result) => (
                <div 
                  key={result.email} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex flex-col">
                    <span className="text-[#2D2D2D] font-medium">{result.displayName}</span>
                    <span className="text-[#928881] text-sm">{result.email}</span>
                  </div>
                  {result.status === 'success' ? (
                    <IonIcon icon={checkmarkCircle} className="w-6 h-6 text-green-500" />
                  ) : result.status === 'error' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-red-500 text-sm">{result.errorMessage}</span>
                      <IonIcon icon={alertCircle} className="w-6 h-6 text-red-500" />
                    </div>
                  ) : (
                    <IonSpinner name="crescent" className="w-6 h-6" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Invite Form View */
          <div className="flex flex-col gap-6">
            {/* Team Logo & Name Section */}
            <div className="flex items-end gap-4">
              {/* Logo Upload */}
              <div className="flex flex-col items-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoChange}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={handleLogoClick}
                  disabled={isUploadingLogo}
                  className="relative w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#D44D5C] transition-colors flex items-center justify-center overflow-hidden bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploadingLogo ? (
                    <IonSpinner name="crescent" className="w-8 h-8" />
                  ) : logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Team logo" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <IonIcon icon={cameraOutline} className="w-8 h-8 text-gray-400" />
                  )}
                </button>
                <span className="text-xs text-[#928881] mt-1">
                  Team Logo
                </span>
              </div>

              {/* Team Name */}
              <div className="flex-1 flex flex-col">
                {isEditingTeamName ? (
                  <div className="flex items-center gap-2">
                    <IonItem className="flex-1" lines="full">
                      <IonInput
                        value={teamName}
                        placeholder="Enter team name"
                        autoFocus={true}
                        onIonInput={(e) => handleTeamNameChange(e.detail.value!)}
                      />
                    </IonItem>
                    <IonButton
                      color="primary"
                      onClick={handleSaveTeamName}
                      disabled={!teamName.trim()}
                      className="flex-none"
                    >
                      Save
                    </IonButton>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between gap-3 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg">
                      <span className="text-[#2D2D2D] text-2xl font-bold">{teamName}</span>
                      <IonButton
                        color="primary"
                        fill="outline"
                        size="small"
                        onClick={handleEditTeamName}
                        className="flex-none"
                      >
                        Edit
                      </IonButton>
                    </div>
                    <span className="text-xs text-[#928881] mt-1">Team Name</span>
                  </div>
                )}
              </div>
            </div>

            {/* Seats Section */}
            <div className="flex items-center justify-between">
              <div className="text-[#D44D5C] text-base font-normal">
                Seats available: {availableSeats} / {totalSeats}
              </div>
              {showAddSeatsButton && (
                <IonButton
                  color="primary"
                  fill="outline"
                  onClick={handleAddSeats}
                >
                  Add Seats
                </IonButton>
              )}
            </div>

            {/* Additional Seats Cost Warning - shown when inviting over available seats */}
            {additionalSeatsNeeded > 0 && hasTeamName && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-[#D44D5C] text-sm">
                  {additionalSeatsNeeded} additional {additionalSeatsNeeded === 1 ? 'seat' : 'seats'} ${seatPrice}/{seatPeriod}
                </p>
              </div>
            )}

            {/* Invite Inputs Section */}
            <div className="flex flex-col gap-4">
              <IonLabel className="text-[#2D2D2D] text-base font-medium">
                Invite Team Members
              </IonLabel>
              {!hasTeamName ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-[#928881] text-sm">
                    Please create a team name above before inviting team members.
                  </p>
                </div>
              ) : (
                inviteInputs.map((invite, index) => (
                  <div key={index} className="flex gap-2 p-3 bg-gray-50 rounded-lg">
                    {/* Left column: Inputs */}
                    <div className="flex-1 flex flex-col gap-2">
                      <IonItem lines="full" disabled={availableSeats === 0}>
                        <IonInput
                          value={invite.displayName}
                          placeholder="Display Name"
                          onIonInput={(e) => handleInputChange(index, 'displayName', e.detail.value!)}
                        />
                      </IonItem>
                      <IonItem lines="full" disabled={availableSeats === 0}>
                        <IonInput
                          type="email"
                          value={invite.email}
                          placeholder="email@example.com"
                          onIonInput={(e) => handleInputChange(index, 'email', e.detail.value!)}
                        />
                      </IonItem>
                    </div>
                    {/* Right column: Button centered */}
                    <div className="flex items-center justify-center" style={{ minWidth: '40px' }}>
                      {index === inviteInputs.length - 1 ? (
                        // Only show plus icon if both email and displayName are filled
                        invite.email.trim() !== '' && invite.displayName.trim() !== '' ? (
                          <IonButton
                            color="primary"
                            fill="outline"
                            size="small"
                            onClick={handleAddInvite}
                          >
                            <IonIcon icon={addOutline} slot="icon-only" />
                          </IonButton>
                        ) : (
                          // Reserve space for the button to prevent layout shift
                          <div style={{ width: '40px', height: '40px' }} />
                        )
                      ) : (
                        <IonButton
                          fill="clear"
                          color="medium"
                          size="small"
                          onClick={() => handleRemoveInvite(index)}
                        >
                          <IonIcon icon={removeOutline} slot="icon-only" />
                        </IonButton>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </IonContent>

      {/* Footer */}
      <div className="px-4 py-3 border-t flex items-center justify-end">
        {showConfirmation ? (
          <IonButton color="primary" onClick={handleDone}>
            Done
          </IonButton>
        ) : (
          hasTeamName && (
            <IonButton
              color="primary"
              onClick={handleInvite}
              disabled={
                isInviting ||
                inviteInputs.every(invite => !invite.email.trim() || !invite.displayName.trim())
              }
            >
              {isInviting ? <IonSpinner name="crescent" /> : 'Invite'}
            </IonButton>
          )
        )}
      </div>
    </IonModal>
  );
}

